import { Suspense, lazy, useState, type FormEvent } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { api, ApiError } from "./api/client";
import { Shell } from "./shell/Shell";
import { Login } from "./screens/Login";
import { MessageLog } from "./screens/MessageLog";
import { Contacts } from "./screens/Contacts";
import { Forms } from "./screens/Forms";
import { Groups } from "./screens/Groups";
import { GroupDetail } from "./screens/GroupDetail";
import { Templates } from "./screens/Templates";
import { Campaigns } from "./screens/Campaigns";
import { Analytics } from "./screens/Analytics";
import { CampaignComposer } from "./screens/CampaignComposer";
import { Domains } from "./screens/Domains";

/**
 * The editor is the one screen loaded on demand.
 *
 * CodeMirror is roughly two thirds of the bundle, and six of the seven screens
 * never touch it. Bundled with the rest it would be downloaded and parsed on
 * every cold load of the message log — the screen this product is built around
 * — to serve the one screen that needs it.
 *
 * Everything else stays eagerly imported. Splitting a screen that costs a few
 * kilobytes buys a loading state and nothing else.
 */
const TemplateEditor = lazy(() =>
  import("./screens/TemplateEditor").then((module) => ({ default: module.TemplateEditor })),
);
import { Suppressions } from "./screens/Suppressions";
import { ApiKeys } from "./screens/ApiKeys";
import { Webhooks } from "./screens/Webhooks";
import { Settings } from "./screens/Settings";
import { Operator } from "./screens/Operator";
import { Teams } from "./screens/Teams";
import { TeamDetail } from "./screens/TeamDetail";
import { AcceptInvitation } from "./screens/AcceptInvitation";
import { LoadingMark } from "./components/LoadingMark";
import { useSession } from "./session";

export function App() {
  const { phase } = useSession();

  // Nothing is rendered against an unknown session. Flashing the dashboard and
  // then bouncing to login looks like a bug, and briefly showing a screen the
  // viewer may not be entitled to is worse than a moment of blankness.
  if (phase === "loading") {
    return (
      <div className="centred boot">
        <LoadingMark />
        <span className="oc-body-sm oc-text-secondary">Loading…</span>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      {/*
        Beside /login rather than inside Authenticated, and that placement is
        the whole point: the person following an invitation may have no account
        yet, and Authenticated would bounce them to the login screen with the
        token dropped. This route reads the invitation signed out and sends them
        to login itself, carrying the token in return_to.
      */}
      <Route path="/invitations/:token" element={<AcceptInvitation />} />
      <Route path="*" element={<Authenticated />} />
    </Routes>
  );
}

function LoginRoute() {
  const { phase } = useSession();
  return phase === "ready" ? <Navigate to="/messages" replace /> : <Login />;
}

function Authenticated() {
  const { phase, session, org } = useSession();
  const location = useLocation();

  if (phase !== "ready") {
    // Where they were headed survives the round trip through login.
    const returnTo = location.pathname + location.search;
    return <Navigate to={`/login?return_to=${encodeURIComponent(returnTo)}`} replace />;
  }

  if (session && session.organizations.length === 0) return <NoOrganization />;

  // Ahead of the shell, not inside it. A scheduled organization is refused by
  // every /v1 route the moment it is scheduled, so rendering the dashboard
  // would give a screen of failed panels with the explanation somewhere off to
  // the side — and the one control that matters, the undo, buried among them.
  if (org?.purge_after) return <PendingDeletion />;

  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Navigate to="/messages" replace />} />
        <Route path="/messages" element={<MessageLog />} />
        <Route path="/messages/:messageId" element={<MessageLog />} />
        <Route path="/contacts" element={<Contacts />} />
        {/* Static segments outrank `:contactId`, so "imports" is never taken
            for a contact id — and contact ids are UUIDs besides. */}
        <Route path="/contacts/imports" element={<Contacts view="imports" />} />
        <Route path="/contacts/imports/:importId" element={<Contacts view="imports" />} />
        <Route path="/contacts/:contactId" element={<Contacts />} />
        <Route path="/groups" element={<Groups />} />
        <Route path="/groups/:groupId" element={<GroupDetail />} />
        <Route path="/forms" element={<Forms />} />
        {/*
          The editor and the composer are screens of their own rather than the
          list rendering a drawer. A drawer is for inspecting a row; these are
          workspaces, and one that overlays the list it came from would give up
          most of the width the editor needs.
        */}
        <Route path="/templates" element={<Templates />} />
        <Route
          path="/templates/:templateId"
          element={
            // Matches how the rest of the dashboard waits: a line of text, no
            // spinner and no skeleton.
            <Suspense
              fallback={
                <div className="content">
                  <span className="oc-body-sm oc-text-secondary">Loading the editor…</span>
                </div>
              }
            >
              <TemplateEditor />
            </Suspense>
          }
        />
        <Route path="/campaigns" element={<Campaigns />} />
        <Route path="/campaigns/:campaignId" element={<CampaignComposer />} />
        <Route path="/analytics" element={<Analytics />} />

        <Route path="/domains" element={<Domains />} />
        <Route path="/suppressions" element={<Suppressions />} />
        <Route path="/keys" element={<ApiKeys />} />
        <Route path="/endpoints" element={<Webhooks />} />
        <Route path="/teams" element={<Teams />} />
        <Route path="/teams/:teamId" element={<TeamDetail />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/tenants" element={<Operator />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Shell>
  );
}

/**
 * A real state, not an error: OAuth creates an account before anyone has
 * invited it anywhere, so a first sign-in lands here.
 *
 * This used to be a dead end that told people to run `opencomms seed`, which is
 * only useful to someone with shell access on the host — that is, not to the
 * person who just signed up. It now creates the organization.
 *
 * The endpoint is POST /auth/organizations rather than anything under /v1,
 * because every versioned route resolves a session against a chosen
 * organization and this is the request that produces the first one.
 */
function NoOrganization() {
  const { session, logout, reload, switchOrg } = useSession();

  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const created = await api.post<{ id: string; name: string }>(
        "/auth/organizations",
        { name: name.trim() },
        { unscoped: true },
      );
      // Selected before the reload, so the dashboard opens in the organization
      // just made rather than making someone pick it out of a list of one.
      switchOrg(created.id);
      await reload();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not reach the server. Check that it is running.",
      );
      setBusy(false);
    }
  };

  return (
    <div className="centred">
      <div className="oc-panel oc-starfield login__card">
        <h1 className="oc-headline-md">Create an organization</h1>
        <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
          <span className="oc-mono-sm">{session?.user.email}</span> is signed in but does not belong
          to one yet. Create it here, or ask an owner of an existing organization to add you.
        </p>

        <form className="stack" onSubmit={(event) => void create(event)}>
          <div className="login__field">
            <label className="oc-label" htmlFor="org-name">
              Organization name
            </label>
            <input
              id="org-name"
              className="oc-input"
              autoFocus
              required
              maxLength={200}
              placeholder="Acme Corp"
              value={name}
              onChange={(event) => setName(event.target.value)}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? "org-name-error" : undefined}
            />
            <span className="oc-body-sm oc-text-muted-aa">
              Your display name. Everything else is configured afterwards.
            </span>
          </div>

          {error && (
            <div className="field-error" id="org-name-error" role="alert">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="oc-btn oc-btn--primary"
            disabled={busy || name.trim() === ""}
          >
            {busy ? "Creating…" : "Create organization"}
          </button>
        </form>

        <button type="button" className="oc-btn oc-btn--sm" onClick={() => void logout()}>
          Sign out
        </button>
      </div>
    </div>
  );
}

/**
 * The organization the dashboard is pointed at is scheduled for deletion.
 *
 * A whole screen rather than a banner, because there is nothing else to do
 * here: the organization stops authenticating the instant it is scheduled, so
 * every panel behind this would be an error. What the screen owes the reader is
 * three things — that it is not gone yet, exactly when it will be, and the one
 * button that undoes it.
 *
 * Restoring is owners-only on the server. An admin who lands here is told who
 * can undo it rather than shown a button that 403s.
 */
function PendingDeletion() {
  const { org, orgId, session, role, reload, switchOrg, logout } = useSession();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const restore = async () => {
    if (!orgId) return;
    setBusy(true);
    setError(null);

    try {
      await api.post(`/auth/organizations/${orgId}/restore`, undefined, { unscoped: true });
      await reload();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not reach the server. Check that it is running.",
      );
      setBusy(false);
    }
  };

  const others = (session?.organizations ?? []).filter((o) => o.id !== orgId && !o.purge_after);

  // Rendered as a date and a countdown together. "In 6 days" is what someone
  // needs to judge urgency; the date is what they need to write down.
  const deadline = org?.purge_after ? new Date(org.purge_after) : null;
  const days = deadline
    ? Math.max(0, Math.ceil((deadline.getTime() - Date.now()) / 86_400_000))
    : null;

  return (
    <div className="centred">
      <div className="oc-panel login__card deletion__card" style={{ borderColor: "var(--oc-danger)" }}>
        <h1 className="oc-headline-md" style={{ color: "var(--oc-danger)" }}>
          {org?.name} is scheduled for deletion
        </h1>

        <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
          Sending has stopped and its API keys no longer work. The data is still intact
          {deadline && (
            <>
              {" "}
              until <strong>{deadline.toLocaleString()}</strong>
              {days !== null && <> — about {days === 1 ? "one day" : `${days} days`} from now</>}
            </>
          )}
          . After that it is deleted permanently, including from the analytics store and object
          storage.
        </p>

        <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
          If it is restored, its scheduled campaigns resume, and any whose time has passed go out
          straight away. Mail that was already waiting to send has been stopped and will not go
          out.
        </p>

        {error && (
          <div className="field-error" role="alert">
            {error}
          </div>
        )}

        {role === "owner" ? (
          <button
            type="button"
            className="oc-btn oc-btn--primary"
            disabled={busy}
            onClick={() => void restore()}
          >
            {busy ? "Restoring…" : "Restore this organization"}
          </button>
        ) : (
          <p className="oc-body-sm oc-text-muted-aa" style={{ margin: 0 }}>
            Only an owner can restore it. Ask one before the deadline.
          </p>
        )}

        {others.length > 0 && (
          <div className="stack">
            <div className="oc-label">Switch to another organization</div>
            {others.map((other) => (
              <button
                key={other.id}
                type="button"
                className="oc-btn oc-btn--sm"
                onClick={() => switchOrg(other.id)}
              >
                {other.name}
              </button>
            ))}
          </div>
        )}

        <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={() => void logout()}>
          Sign out
        </button>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="content">
      <div className="oc-panel oc-starfield" style={{ flex: 1 }}>
        <div className="oc-empty">
          <div className="oc-empty__title">No such screen</div>
          <div className="oc-empty__hint">The address does not match anything in the dashboard.</div>
        </div>
      </div>
    </div>
  );
}
